import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { faro } from "@grafana/faro-web-sdk";
import { usePlatformCapabilities } from "@shared-core/capabilities/context";
import { useCustomerDataAdapter } from "@shared-core/data/CustomerDataAdapterContext";
import type { PortalNotice } from "@shared-core/data/customer-api-types";
import { useListQuery } from "@shared-core/domain/useListQuery";
import { usePortalViewer } from "../PortalViewerContext";
import {
  PortalThemeContext,
  type PortalThemeForViewer,
} from "./PortalThemeContext";

/** The look a customer's portal has without a theme: index.html's own title and icon. */
const PORTAL_TITLE = "Pollards — orders and spend";

/**
 * Reads the portal theme for whoever the portal is being shown to (the signed-in person, or the staff view's target
 * through its mirror) and provides it to the chrome and pages. A failed read leaves the Pollards look and is reported
 * to Faro with no content; the feature switched off (a 404) is not a failure, and is quiet. A pop-up closes for this
 * visit at once, and for good only where the read named a dismiss URL (never on the staff mirror).
 */
export function PortalThemeScope({
  brandsDocument = false,
  loading = null,
  children,
}: {
  /** Name the browser tab and give it the customer's icon: the customer's own portal, not the staff view. */
  brandsDocument?: boolean;
  /** Shown while the read is on its way, so no page flashes in the wrong look; null renders the children at once. */
  loading?: ReactNode;
  children: ReactNode;
}) {
  const adapter = useCustomerDataAdapter();
  const capabilities = usePlatformCapabilities();
  const { viewKey } = usePortalViewer();
  const { state } = useListQuery(
    () =>
      adapter.getPortalTheme().catch((err: unknown) => {
        faro.api?.pushEvent("portal_theme.read.failed", {
          status: String(
            (err as { status?: unknown } | null)?.status ?? "network",
          ),
        });
        return null;
      }),
    [adapter, viewKey],
  );
  const [closed, setClosed] = useState<ReadonlySet<string>>(new Set());

  const read = state.status === "success" ? state.data : null;
  const values = read?.theme.values ?? null;
  useEffect(() => {
    if (!brandsDocument || !values) return;
    capabilities.setDocumentBranding({
      title: values.display_name,
      faviconUrl: values.favicon_image,
    });
    return () =>
      capabilities.setDocumentBranding({
        title: PORTAL_TITLE,
        faviconUrl: null,
      });
  }, [brandsDocument, capabilities, values]);

  const dismissNotice = useCallback(
    (notice: PortalNotice) => {
      setClosed((prev) => new Set(prev).add(notice.id));
      if (notice.dismiss_url) {
        adapter.dismissPortalNotice(notice.dismiss_url).catch(() => {
          // Closed for this visit either way; it may show again next time.
          faro.api?.pushEvent("portal_theme.notice_dismiss.failed", {
            notice_id: notice.id,
          });
        });
      }
    },
    [adapter],
  );

  const value = useMemo<PortalThemeForViewer>(
    () => ({
      isLoading: state.status === "loading",
      theme: read?.theme ?? null,
      notices: (read?.notices ?? []).filter((n) => !closed.has(n.id)),
      dismissNotice,
    }),
    [state.status, read, closed, dismissNotice],
  );

  if (state.status === "loading" && loading !== null) return <>{loading}</>;
  return (
    <PortalThemeContext.Provider value={value}>
      {children}
    </PortalThemeContext.Provider>
  );
}
